import React, { useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import SectionNavPills from '../components/SectionNavPills'

const PrivacyPolicy = () => {
  const location = useLocation()

  // Support deep-linking to sections (e.g. /privacy-policy#cookies from the
  // cookie banner "Viac info" link). React Router doesn't scroll to hash
  // anchors natively, so do it once after mount.
  useEffect(() => {
    if (!location.hash) return
    const id = location.hash.slice(1)
    const el = document.getElementById(id)
    if (el) {
      // Small delay so the page has laid out before scrolling.
      setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50)
    }
  }, [location.hash])

  return (
    <div className="min-h-screen bg-gray-50">
      <SectionNavPills />
      <div className="container mx-auto px-4 py-14 md:py-20">
        <div className="max-w-4xl mx-auto">
          <header className="mb-6 md:mb-8 text-center">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              Ochrana osobných údajov
            </h1>
            <p className="text-sm sm:text-base text-gray-600 leading-relaxed max-w-2xl mx-auto">
              Informácie o spracúvaní vašich osobných údajov v službe EduMisia. Posledná aktualizácia: február 2026.
            </p>
          </header>

          <div className="bg-white rounded-2xl border border-gray-100 ring-1 ring-gray-50 shadow-sm p-6 sm:p-8 md:p-10 space-y-8">

            {/* V1 review notice (soft) */}
            <div className="p-4 bg-blue-50 border-l-4 border-blue-300 rounded">
              <p className="text-sm text-blue-900">
                Toto je verzia 1 zásad ochrany osobných údajov. Text môže byť dopracovaný po právnej revízii. V prípade otázok nás kontaktujte na{' '}
                <a href="mailto:podpora@edumisia.sk" className="font-medium underline">podpora@edumisia.sk</a>.
              </p>
            </div>

            {/* Intro */}
            <section>
              <div className="text-gray-700 space-y-3">
                <p>
                  EduMisia je online vzdelávacia platforma na prípravu na Testovanie 9. Spracúvame údaje potrebné na vytvorenie účtu, prístup k testom, vyhodnotenie výsledkov, platby a komunikáciu s používateľom.
                </p>
                <p>
                  Platby sú spracúvané prostredníctvom platobnej služby Stripe. Autentifikácia a používateľské účty sú zabezpečené prostredníctvom Supabase.
                </p>
                <p className="text-sm text-gray-600">
                  Službu môžu používať aj maloletí. Balíček však môže zakúpiť iba osoba staršia ako 18 rokov, alebo rodič či iný zákonný zástupca maloletého používateľa.
                </p>
              </div>
            </section>

            {/* 1. Prevádzkovateľ */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                1. Prevádzkovateľ osobných údajov
              </h2>
              <div className="text-gray-700 space-y-3">
                <p>Prevádzkovateľom vašich osobných údajov je:</p>
                <div className="pl-4 space-y-1">
                  <p><strong>Mgr. Vladislava Pajerová</strong></p>
                  <p>Lipová 6459/4</p>
                  <p>974 09 Banská Bystrica</p>
                  <p>Slovenská republika</p>
                  <p className="pt-2">IČO: 51360454</p>
                  <p>Vedený v registri: Okresný úrad Banská Bystrica</p>
                  <p>Číslo živnostenského registra: 430-58126</p>
                  <p className="pt-2">
                    Email: <a href="mailto:podpora@edumisia.sk" className="text-blue-600">podpora@edumisia.sk</a>
                  </p>
                </div>
              </div>
            </section>

            {/* 2. Aké údaje spracúvame */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                2. Aké osobné údaje spracúvame
              </h2>
              <div className="text-gray-700 space-y-3">
                <p>Pri používaní našich služieb môžeme spracúvať nasledujúce kategórie osobných údajov:</p>

                <div className="pl-4 space-y-3">
                  <div>
                    <h3 className="font-semibold mb-2">Registračné údaje</h3>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li>email (používaný na prihlásenie),</li>
                      <li>heslo (uložené v podobe bezpečného hashu, nie v čitateľnej forme).</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-semibold mb-2">Údaje o používaní služby</h3>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li>výsledky testov a skóre,</li>
                      <li>dátum a čas dokončenia testov,</li>
                      <li>história prístupu k testom.</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-semibold mb-2">Platobné údaje</h3>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li>informácie o platbách (spracúvané prostredníctvom Stripe),</li>
                      <li>história zakúpených balíčkov.</li>
                    </ul>
                    <p className="text-sm text-gray-600 mt-2 ml-4">
                      Platobné karty a citlivé platobné údaje neukladáme priamo — spracúva ich platobná brána Stripe.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* 3. Účel spracovania */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                3. Účel a právny základ spracovania
              </h2>
              <div className="text-gray-700 space-y-3">
                <p>Vaše osobné údaje spracúvame na nasledujúce účely:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li><strong>Poskytovanie služieb</strong> – správa účtu, prístup k testom (právny základ: plnenie zmluvy).</li>
                  <li><strong>Spracovanie platieb</strong> – vybavenie zakúpenia balíčka (právny základ: plnenie zmluvy).</li>
                  <li><strong>Technická podpora a komunikácia</strong> – riešenie problémov a odpovedanie na otázky (právny základ: plnenie zmluvy a oprávnený záujem).</li>
                  <li><strong>Zlepšovanie služieb</strong> – analýza používania (právny základ: oprávnený záujem).</li>
                </ul>
              </div>
            </section>

            {/* 4. Doba uchovávania */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                4. Doba uchovávania údajov
              </h2>
              <div className="text-gray-700 space-y-3">
                <p>Osobné údaje uchovávame:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li><strong>Počas trvania účtu</strong> – registračné a používateľské údaje.</li>
                  <li><strong>Po zrušení účtu</strong> – po dobu nevyhnutnú na splnenie zákonných povinností (najmä účtovných a daňových).</li>
                  <li><strong>Platobné údaje</strong> – v rozsahu a po dobu vyžadovanú právnymi predpismi (najmä účtovnými a daňovými).</li>
                </ul>
              </div>
            </section>

            {/* 5. Vaše práva */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                5. Vaše práva podľa GDPR
              </h2>
              <div className="text-gray-700 space-y-3">
                <p>Máte právo:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li><strong>Prístup k údajom</strong> – požiadať o kópiu svojich osobných údajov.</li>
                  <li><strong>Oprava</strong> – opraviť nesprávne alebo neaktuálne údaje.</li>
                  <li><strong>Výmaz</strong> – požiadať o vymazanie svojich údajov („právo byť zabudnutý").</li>
                  <li><strong>Obmedzenie spracovania</strong> – obmedziť spracovanie za určitých podmienok.</li>
                  <li><strong>Prenosnosť</strong> – získať údaje v štruktúrovanom formáte.</li>
                  <li><strong>Námietka</strong> – namietať proti spracovaniu na základe oprávneného záujmu.</li>
                  <li><strong>Sťažnosť</strong> – podať sťažnosť na Úrad na ochranu osobných údajov SR.</li>
                </ul>
                <p className="mt-4">
                  Pre uplatnenie svojich práv nás kontaktujte na{' '}
                  <a href="mailto:podpora@edumisia.sk" className="text-blue-600">podpora@edumisia.sk</a>.
                </p>
              </div>
            </section>

            {/* 6. Zabezpečenie */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                6. Zabezpečenie údajov
              </h2>
              <div className="text-gray-700 space-y-3">
                <p>Na ochranu vašich osobných údajov používame:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>šifrovanie prenosu údajov (HTTPS/TLS),</li>
                  <li>bezpečné ukladanie hesiel (hashing),</li>
                  <li>pravidelné bezpečnostné aktualizácie,</li>
                  <li>obmedzený prístup k údajom len pre oprávnené osoby.</li>
                </ul>
              </div>
            </section>

            {/* 7. Tretie strany */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                7. Zdieľanie údajov s tretími stranami
              </h2>
              <div className="text-gray-700 space-y-3">
                <p>Vaše údaje môžeme zdieľať s:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li><strong>Stripe</strong> – spracovanie platieb (platobná brána s vlastnými zásadami ochrany osobných údajov).</li>
                  <li><strong>Supabase</strong> – autentifikácia a hosting databázy (infraštruktúrny poskytovateľ).</li>
                  <li><strong>Resend</strong> – odosielanie e-mailov (napr. potvrdenia a notifikácie).</li>
                </ul>
                <p className="mt-3 text-sm">
                  Všetci poskytovatelia sú zmluvne alebo právne zaviazaní dodržiavať GDPR a zabezpečiť vaše údaje.
                </p>
              </div>
            </section>

            {/* 8. Cookies */}
            <section id="cookies" className="scroll-mt-24">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                8. Cookies a sledovacie technológie
              </h2>
              <div className="text-gray-700 space-y-3">
                <p>Používame dve kategórie cookies:</p>
                <p className="font-semibold text-gray-900">Nevyhnutné cookies (bez súhlasu)</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>prihlásenie a udržanie prihlasovacej relácie,</li>
                  <li>bezpečnosť a ochrana pred zneužitím,</li>
                  <li>základná funkčnosť webu.</li>
                </ul>
                <p className="font-semibold text-gray-900">Analytické cookies (iba s vaším súhlasom)</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>slúžia na meranie návštevnosti a zlepšovanie EduMisie,</li>
                  <li>nástroj: Google Analytics 4 (Consent Mode v2 s IP anonymizáciou),</li>
                  <li>aktivujú sa až po kliknutí „Súhlasím" v cookie lište,</li>
                  <li>neposielame do nich osobné údaje ako e-mail, meno, telefón, obsah spätnej väzby, ID používateľa, ani ID platby.</li>
                </ul>
                <p className="font-semibold text-gray-900">Reklamné cookies</p>
                <p>Reklamné ani remarketingové cookies <span className="font-semibold">nepoužívame</span>. V Google Consent Mode udržiavame <code className="text-xs bg-slate-100 px-1 rounded">ad_storage</code>, <code className="text-xs bg-slate-100 px-1 rounded">ad_user_data</code> a <code className="text-xs bg-slate-100 px-1 rounded">ad_personalization</code> trvalo zamietnuté.</p>
                <p className="text-sm text-gray-600">
                  Svoju voľbu môžete kedykoľvek zmeniť kliknutím na odkaz <span className="font-semibold">„Nastavenia cookies"</span> v spodnej časti stránky.
                </p>
              </div>
            </section>

            {/* 9. Kontakt */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                9. Kontakt
              </h2>
              <div className="text-gray-700">
                <p className="mb-3">
                  Ak máte otázky týkajúce sa ochrany osobných údajov, kontaktujte nás:
                </p>
                <div className="pl-4">
                  <p>
                    Email: <a href="mailto:podpora@edumisia.sk" className="text-blue-600">podpora@edumisia.sk</a>
                  </p>
                  <p className="mt-2">
                    alebo navštívte našu <Link to="/contact" className="text-blue-600 hover:text-blue-700">kontaktnú stránku</Link>.
                  </p>
                </div>
              </div>
            </section>

          </div>

          {/* Back to Home */}
          <div className="mt-8 text-center">
            <Link
              to="/"
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              ← Späť na hlavnú stránku
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PrivacyPolicy
